import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { dashboardBffClient } from '@/api-client';
import type { PolicyP, RunActionP } from '@/api-client/api';
import { permissionsErrorHandler } from '@/authorization';
import { useApiRequest } from '@/composables/useApiRequest';
import type { Poller } from '@/composables/usePolling';
import usePolling from '@/composables/usePolling';
import constants from '@/constants';
import {
  toGlobalPolicyWithTableSummary,
  toPolicyWithTableSummary,
  toPolicyWithViewSummary,
} from '@/pages/policies/new/model';
import {
  POLICIES_POLLING_INTERVAL,
  POLICY_POLLING_INTERVAL,
} from '@/pages/policies/new/shared/constants';
import { handlePolicyError } from '@/pages/policies/policy-utils';
import { useCustomAccessRights } from '@/stores/accessRights/customAccessRights';
import { useAppInfoStore } from '@/stores/appInfo';

export type PoliciesEndpointQuery = Parameters<typeof dashboardBffClient.bff.bffListPolicyCRs>[1];
export type PolicyValidationEndpointQuery = Parameters<
  typeof dashboardBffClient.bff.bffValidatePolicyCr
>[1];
export type PolicyValidationResponse = { valid: boolean; message: string };

export const usePolicyStore = defineStore('policy', () => {
  const appInfoStore = useAppInfoStore();
  const customAccessRightsStore = useCustomAccessRights();
  const policiesPageFirstLoad = ref<boolean | undefined>(true);
  const policyDetailsPageFirstLoad = ref<boolean | undefined>(true);
  const policiesQueriedNamespace = ref<string | undefined>(undefined);
  const queriedPolicyName = ref<string | undefined>(undefined);
  const queriedPolicyNamespace = ref<string | undefined>(undefined);
  const _policiesPoller = ref<Poller>();
  const _policyPoller = ref<Poller>();

  const {
    request: _fetchPolicies,
    getResponseByArgs: getPoliciesResponseByArgs,
    forcefullyRequest: _forceFetchPolicies,
    updateByArgs: updatePoliciesByArgs,
  } = useApiRequest(dashboardBffClient.bff.bffGetPoliciesDetails, {
    transformResponse: (response) => response.data,
    cacheKey: (namespace) => namespace,
    cacheTimeout: 30 * 1000,
    onFulfilled: (_, namespace) => {
      policiesPageFirstLoad.value = false;

      if (policiesQueriedNamespace.value !== namespace) {
        policiesQueriedNamespace.value = namespace;
        customAccessRightsStore.resetCustomAccessRight('list', 'policies');
      }

      customAccessRightsStore.setCustomAccessRight('list', 'policies', true);
    },
    onRejected: (err, namespace) => {
      policiesPageFirstLoad.value = false;

      if (policiesQueriedNamespace.value !== namespace) {
        policiesQueriedNamespace.value = namespace;
        customAccessRightsStore.resetCustomAccessRight('list', 'policies');
      }

      permissionsErrorHandler(err, 'policies', 'list');
    },
  });

  const {
    request: _fetchPolicy,
    isPending: isPolicyFetchPending,
    forcefullyRequest: _forceFetchPolicy,
    getResponseByArgs: getPolicyResponseByArgs,
    updateByArgs: updatePolicyByArgs,
  } = useApiRequest(dashboardBffClient.bff.bffGetPolicyCr, {
    transformResponse: (response) => toPolicyWithViewSummary(response.data.policy!),
    cacheKey: (namespace, name) => `${namespace}/${name}`,
    cacheTimeout: 30 * 1000,
    onFulfilled: (_, namespace, name) => {
      policyDetailsPageFirstLoad.value = false;

      if (policiesQueriedNamespace.value !== namespace || queriedPolicyName.value !== name) {
        policiesQueriedNamespace.value = namespace;
        queriedPolicyName.value = name;
        customAccessRightsStore.resetCustomAccessRight('get', 'policies');
      }

      customAccessRightsStore.setCustomAccessRight('get', 'policies', true);
    },
    onRejected: (err, namespace, name) => {
      policyDetailsPageFirstLoad.value = false;

      if (queriedPolicyNamespace.value !== namespace || queriedPolicyName.value !== name) {
        queriedPolicyNamespace.value = namespace;
        queriedPolicyName.value = name;
        customAccessRightsStore.resetCustomAccessRight('get', 'policies');
      }

      permissionsErrorHandler(err, 'policies', 'get');
    },
  });

  const route = useRoute();

  const isGlobalContext = ref<boolean | undefined>(undefined);

  const namespaceQuery = computed(() => route.query.namespace as string | undefined);

  const policy = computed(
    () => (namespace: string, name: string) => getPolicyResponseByArgs(namespace, name),
  );

  const policies = computed(() =>
    getPoliciesResponseByArgs(appInfoStore.systemNamespace)?.map(toPolicyWithTableSummary),
  );

  const policiesGlobal = computed(() =>
    getPoliciesResponseByArgs(constants.MULTICLUSTER_NAMESPACE)?.map(
      toGlobalPolicyWithTableSummary,
    ),
  );

  const policiesByNamespace = computed(
    () => (namespace: string) =>
      getPoliciesResponseByArgs(namespace)?.map(toPolicyWithTableSummary),
  );

  const unauthorizedToViewPolicyLastRuns = computed(() =>
    policies.value
      ? policies.value.every(
          (p) => p.tableSummary.lastRun && p.tableSummary.lastRun.unauthorized === true,
        )
      : false,
  );

  const policiesPoller = computed(() => _policiesPoller.value);
  const policyPoller = computed(() => _policyPoller.value);

  async function fetchPolicy(namespace: string, name: string) {
    await _fetchPolicy(namespace, name);
  }

  async function forceFetchPolicy(namespace: string, name: string) {
    await _forceFetchPolicy(namespace, name);
  }

  async function fetchPolicies(query?: PoliciesEndpointQuery) {
    await _fetchPolicies(appInfoStore.systemNamespace, query);
  }

  async function forceFetchPolicies(query?: PoliciesEndpointQuery) {
    await _forceFetchPolicies(appInfoStore.systemNamespace, query);
  }

  async function fetchGlobalPolicies(query?: PoliciesEndpointQuery) {
    await _fetchPolicies(constants.MULTICLUSTER_NAMESPACE, query);
  }

  async function forceFetchGlobalPolicies(query?: PoliciesEndpointQuery) {
    await _forceFetchPolicies(constants.MULTICLUSTER_NAMESPACE, query);
  }

  async function fetchPoliciesByNamespace(namespace: string, query?: PoliciesEndpointQuery) {
    await _fetchPolicies(namespace, query);
  }

  async function forceFetchPoliciesByNamespace(namespace: string, query?: PoliciesEndpointQuery) {
    await _forceFetchPolicies(namespace, query);
  }

  async function validatePolicy(
    policy: PolicyP,
    params?: PolicyValidationEndpointQuery,
  ): Promise<PolicyValidationResponse | undefined> {
    try {
      await dashboardBffClient.bff.bffValidatePolicyCr({ policy }, params);
      return { valid: true, message: '' };
    } catch (err) {
      if (err.response?.status === 400) {
        return {
          valid: false,
          message: err.response?.data?.message?.replace('Bad Request. Cause: ', '') ?? '',
        };
      }

      handlePolicyError(err);
    }
  }

  async function updatePolicy(policy: PolicyP) {
    return await dashboardBffClient.bff.bffUpdatePolicyCr(policy.metadata!.namespace!, {
      policy,
    });
  }

  async function runOnce(policyNamespace: string, policyName: string, snapshotExpiry?: Date) {
    const payload: RunActionP = {
      metadata: {
        generateName: 'policy-run-',
      },
      spec: {
        subject: {
          name: policyName,
          namespace: policyNamespace,
        },
        expiresAt: snapshotExpiry?.toISOString(),
      },
    };

    return await dashboardBffClient.runaction.runAction(payload);
  }

  async function deletePolicy(policyNamespace: string, policyName: string) {
    return await dashboardBffClient.bff.bffDeletePolicyCr(policyNamespace, policyName);
  }

  async function pausePolicy(policy: PolicyP) {
    const pausedPolicy = { ...policy, spec: { ...policy.spec, paused: true } };
    return await updatePolicy(pausedPolicy);
  }

  async function resumePolicy(policy: PolicyP) {
    const resumedPolicy = { ...policy, spec: { ...policy.spec, paused: undefined } };
    return await updatePolicy(resumedPolicy);
  }

  async function revalidatePolicy(policyNamespace: string, policyName: string) {
    return await dashboardBffClient.validateExistingPolicy.validateExistingPolicy(
      policyNamespace,
      policyName,
    );
  }

  async function upgradePolicy(policyNamespace: string, policyName: string) {
    return await dashboardBffClient.bff.upgradePolicy(policyNamespace, policyName);
  }

  /**
   * Forcefully fetch policies based on the current context. This should be used after calls that modify the policies.
   */
  async function forceFetchPoliciesByNamespaceContext() {
    if (namespaceQuery.value) {
      await forceFetchPoliciesByNamespace(namespaceQuery.value);
    } else {
      isGlobalContext.value ? await forceFetchGlobalPolicies() : await forceFetchPolicies();
    }
  }

  type CreatePoliciesPollerOptions = {
    namespace?: string;
    interval?: number;
    isGlobalContext?: boolean;
  };

  function createPoliciesPoller({
    namespace,
    isGlobalContext,
    interval = POLICIES_POLLING_INTERVAL,
  }: CreatePoliciesPollerOptions = {}) {
    _policiesPoller.value = usePolling(() => {
      return namespace
        ? forceFetchPoliciesByNamespace(namespace)
        : isGlobalContext
          ? forceFetchGlobalPolicies()
          : forceFetchPolicies();
    }, interval);
  }

  function destroyPoliciesPoller() {
    _policiesPoller.value?.stopPolling();
    _policiesPoller.value = undefined;
  }

  type CreatePolicyPollerOptions = {
    namespace: string;
    name: string;
    interval?: number;
  };

  function createPolicyPoller({
    namespace,
    name,
    interval = POLICY_POLLING_INTERVAL,
  }: CreatePolicyPollerOptions) {
    _policyPoller.value = usePolling(() => forceFetchPolicy(namespace, name), interval);
  }

  function destroyPolicyPoller() {
    _policyPoller.value?.stopPolling();
    _policyPoller.value = undefined;
  }

  function $reset() {
    isGlobalContext.value = undefined;
    policiesPageFirstLoad.value = undefined;
    policyDetailsPageFirstLoad.value = undefined;
  }

  return {
    isPolicyFetchPending,
    policiesPageFirstLoad,
    policyDetailsPageFirstLoad,
    policy,
    isGlobalContext,
    policies,
    fetchPolicies,
    fetchPolicy,
    forceFetchPolicy,
    forceFetchPolicies,
    policiesGlobal,
    fetchGlobalPolicies,
    forceFetchGlobalPolicies,
    policiesByNamespace,
    fetchPoliciesByNamespace,
    forceFetchPoliciesByNamespace,
    forceFetchPoliciesByNamespaceContext,
    validatePolicy,
    updatePolicy,
    runOnce,
    deletePolicy,
    pausePolicy,
    resumePolicy,
    revalidatePolicy,
    upgradePolicy,
    updatePoliciesByArgs,
    updatePolicyByArgs,
    unauthorizedToViewPolicyLastRuns,
    policiesPoller,
    policyPoller,
    createPoliciesPoller,
    createPolicyPoller,
    destroyPoliciesPoller,
    destroyPolicyPoller,
    $reset,
  };
});
